// src/authFetch.js
// Drop-in replacement for `fetch` that attaches an Entra ID access token
// (Authorization: Bearer <token>) scoped to THIS app's backend API.
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance } from "./msalInstance";
import { apiRequest } from "./authConfig";

/**
 * Thrown when a backend call needs interactive sign-in that cannot be completed
 * silently. authFetch does NOT redirect from within a data call; instead it
 * throws this (preserving in-progress page state) and signals the app to show a
 * reconnect prompt driven by a user click (see ReconnectPrompt).
 */
export class InteractiveAuthRequiredError extends Error {
    constructor(cause) {
        super("Interactive authentication is required to continue.");
        this.name = "InteractiveAuthRequiredError";
        this.cause = cause;
    }
}

/** Window event the app listens for to show the reconnect prompt. */
export const AUTH_INTERACTION_REQUIRED_EVENT = "auth:interaction-required";

/**
 * True if the error is the "interactive sign-in required" signal from authFetch.
 * Callers can use this in their catch blocks to skip normal error UI (a reconnect
 * prompt is already being shown for it).
 */
export function isInteractiveAuthError(error) {
    return error instanceof InteractiveAuthRequiredError ||
        error?.name === "InteractiveAuthRequiredError";
}

function signalInteractionRequired() {
    window.dispatchEvent(new CustomEvent(AUTH_INTERACTION_REQUIRED_EVENT));
}

/**
 * Acquire an access token for the backend API silently if possible.
 * Does NOT redirect; throws InteractiveAuthRequiredError when interaction is
 * needed so the current page and any in-progress work are preserved.
 * @returns {Promise<string>} the access token
 */
export async function getApiToken() {
    const account =
        msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

    if (!account) {
        signalInteractionRequired();
        throw new InteractiveAuthRequiredError();
    }

    try {
        const result = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account,
        });
        return result.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            signalInteractionRequired();
            throw new InteractiveAuthRequiredError(error);
        }
        throw error;
    }
}

/**
 * fetch() wrapper that adds the bearer token for backend API calls.
 * Same signature as fetch(input, init). Throws InteractiveAuthRequiredError
 * (without firing the request) when the session must be renewed interactively.
 */
export async function authFetch(input, init = {}) {
    const token = await getApiToken();
    const headers = new Headers(init.headers || {});
    headers.set("Authorization", `Bearer ${token}`);
    return fetch(input, { ...init, headers });
}

/**
 * Interactive re-authentication, triggered by a user gesture (button click).
 * Tries a popup first to preserve page state; falls back to a full-page
 * redirect if the popup is blocked or fails.
 * @returns {Promise<string|null>} access token, or null if a redirect started.
 */
export async function reconnect() {
    try {
        const result = await msalInstance.acquireTokenPopup(apiRequest);
        return result.accessToken;
    } catch (error) {
        await msalInstance.acquireTokenRedirect(apiRequest);
        return null;
    }
}
